import { useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchProducts, absoluteUrl } from "@/lib/api";

export default function ProductSlider() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const autoplayRef = useRef(null);

  useEffect(() => {
    fetchProducts()
      .then((data) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    autoplayRef.current = setInterval(() => emblaApi.scrollNext(), 3500);
    return () => clearInterval(autoplayRef.current);
  }, [emblaApi, products]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-blue-600 mb-3">Our Product Portfolio</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900">
            Medical Supplies <span className="text-emerald-600">Division</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed text-base">
            As part of our commitment to supplying essential, fast-moving goods, High Roller Consumer Goods Trading has expanded its distribution network to include high-quality medical supplies. We partner with trusted manufacturers to ensure hospitals, clinics, and corporate partners have reliable access to critical health products.
          </p>
        </motion.div>
          <div className="flex gap-2 self-end">
            <button
              data-testid="products-prev"
              onClick={scrollPrev}
              className="w-11 h-11 rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="products-next"
              onClick={scrollNext}
              className="w-11 h-11 rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center transition-colors"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div data-testid="products-loading" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="overflow-hidden -mx-2" ref={emblaRef} data-testid="products-carousel">
            <div className="flex">
              {products.map((p) => (
                <div key={p.id} className="px-2 shrink-0 grow-0 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/5">
                  <article
                    data-testid={`product-card-${p.id}`}
                    className="group bg-white rounded-2xl border border-slate-200 hover:border-blue-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full flex flex-col"
                  >
                    <div className="aspect-square bg-slate-50 overflow-hidden relative">
                      <img src={absoluteUrl(p.image_url)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-blue-700 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">{p.category}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 leading-snug">{p.name}</h3>
                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">{p.description}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
